import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  CheckCircle as CompleteIcon,
} from '@mui/icons-material';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import EmptyState from '../components/data-display/EmptyState';
import { InvestigationStepForm } from '../components/investigations/InvestigationStepForm';
import { investigationService } from '../services/investigationService';
import { useNotificationStore } from '../store/notificationStore';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { ROUTES } from '../utils/routes';
import {
  IncidentSeverities,
  InvestigationStatuses,
  InvestigationStepTypes,
  InvestigationStepStatuses,
  MessageSenderTypes,
  type IncidentInvestigationSessionDto,
  type IncidentInvestigationStepDto,
  type IncidentInvestigationMessageDto,
  type IncidentCorrelationDto,
  type InvestigationStepStatus,
  type InvestigationStepType,
} from '../types/investigation';

const severityLabel = (severity: number): string => {
  switch (severity) {
    case IncidentSeverities.Critical:
      return 'Critical';
    case IncidentSeverities.High:
      return 'High';
    case IncidentSeverities.Medium:
      return 'Medium';
    case IncidentSeverities.Low:
      return 'Low';
    default:
      return 'Unknown';
  }
};

const severityColor = (severity: number): 'default' | 'success' | 'info' | 'warning' | 'error' => {
  switch (severity) {
    case IncidentSeverities.Critical:
      return 'error';
    case IncidentSeverities.High:
      return 'warning';
    case IncidentSeverities.Medium:
      return 'info';
    case IncidentSeverities.Low:
      return 'success';
    default:
      return 'default';
  }
};

const statusLabel = (status: number): string => {
  switch (status) {
    case InvestigationStatuses.Completed:
      return 'Completed';
    case InvestigationStatuses.InProgress:
      return 'In Progress';
    case InvestigationStatuses.Paused:
      return 'Paused';
    case InvestigationStatuses.Cancelled:
      return 'Cancelled';
    case InvestigationStatuses.NotStarted:
      return 'Not Started';
    default:
      return 'Unknown';
  }
};

const statusColor = (status: number): 'default' | 'success' | 'info' | 'warning' | 'error' => {
  switch (status) {
    case InvestigationStatuses.Completed:
      return 'success';
    case InvestigationStatuses.InProgress:
      return 'info';
    case InvestigationStatuses.Paused:
      return 'warning';
    case InvestigationStatuses.Cancelled:
      return 'error';
    default:
      return 'default';
  }
};

const stepTypeLabel = (type: InvestigationStepType): string => {
  switch (type) {
    case InvestigationStepTypes.DataCollection:
      return 'Data Collection';
    case InvestigationStepTypes.Analysis:
      return 'Analysis';
    case InvestigationStepTypes.Hypothesis:
      return 'Hypothesis';
    case InvestigationStepTypes.Verification:
      return 'Verification';
    case InvestigationStepTypes.Conclusion:
      return 'Conclusion';
    default:
      return 'Unknown';
  }
};

const stepStatusLabel = (status: InvestigationStepStatus): string => {
  switch (status) {
    case InvestigationStepStatuses.Pending:
      return 'Pending';
    case InvestigationStepStatuses.InProgress:
      return 'In Progress';
    case InvestigationStepStatuses.Completed:
      return 'Completed';
    case InvestigationStepStatuses.Skipped:
      return 'Skipped';
    case InvestigationStepStatuses.Failed:
      return 'Failed';
    default:
      return 'Unknown';
  }
};

const stepStatusColor = (status: InvestigationStepStatus): 'default' | 'success' | 'info' | 'warning' | 'error' => {
  switch (status) {
    case InvestigationStepStatuses.Completed:
      return 'success';
    case InvestigationStepStatuses.InProgress:
      return 'info';
    case InvestigationStepStatuses.Pending:
      return 'warning';
    case InvestigationStepStatuses.Failed:
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Investigation detail page component (Module 24).
 */
export const InvestigationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  const [investigation, setInvestigation] = useState<IncidentInvestigationSessionDto | null>(null);
  const [steps, setSteps] = useState<IncidentInvestigationStepDto[]>([]);
  const [messages, setMessages] = useState<IncidentInvestigationMessageDto[]>([]);
  const [correlations, setCorrelations] = useState<IncidentCorrelationDto[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addStepOpen, setAddStepOpen] = useState(false);

  const loadAll = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await investigationService.getInvestigation(id);
      setInvestigation(inv);

      const [s, m, c] = await Promise.all([
        investigationService.getInvestigationSteps(id),
        investigationService.getInvestigationMessages(id),
        investigationService.getInvestigationCorrelations(id),
      ]);
      setSteps(s);
      setMessages(m);
      setCorrelations(c);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load investigation';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleComplete = async () => {
    if (!id) return;
    try {
      await investigationService.completeInvestigation(id);
      showNotification('Investigation completed successfully', 'success');
      await loadAll();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to complete investigation';
      showNotification(msg, 'error');
    }
  };

  const stepColumns: DataTableColumn<IncidentInvestigationStepDto>[] = useMemo(
    () => [
      {
        id: 'order',
        label: '#',
        minWidth: 60,
        render: (s) => <Typography variant="body2">{s.order}</Typography>,
      },
      {
        id: 'name',
        label: 'Step',
        minWidth: 240,
        render: (s) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {s.stepName}
            </Typography>
            {s.description && (
              <Typography variant="caption" color="text.secondary">
                {s.description.length > 90 ? `${s.description.slice(0, 90)}…` : s.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        id: 'type',
        label: 'Type',
        minWidth: 150,
        render: (s) => <Chip label={stepTypeLabel(s.stepType)} size="small" variant="outlined" />,
      },
      {
        id: 'status',
        label: 'Status',
        minWidth: 140,
        render: (s) => <Chip label={stepStatusLabel(s.status)} size="small" color={stepStatusColor(s.status)} />,
      },
      {
        id: 'started',
        label: 'Started',
        minWidth: 170,
        render: (s) =>
          s.startedAt ? (
            <Typography variant="body2">{new Date(s.startedAt).toLocaleString()}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        id: 'completed',
        label: 'Completed',
        minWidth: 170,
        render: (s) =>
          s.completedAt ? (
            <Typography variant="body2">{new Date(s.completedAt).toLocaleString()}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
    ],
    []
  );

  const messageColumns: DataTableColumn<IncidentInvestigationMessageDto>[] = useMemo(
    () => [
      {
        id: 'seq',
        label: '#',
        minWidth: 60,
        render: (m) => <Typography variant="body2">{m.sequenceNumber}</Typography>,
      },
      {
        id: 'sender',
        label: 'Sender',
        minWidth: 110,
        render: (m) => (
          <Chip
            label={m.senderType === MessageSenderTypes.Agent ? 'Agent' : 'User'}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        id: 'analysisType',
        label: 'Type',
        minWidth: 140,
        render: (m) => <Typography variant="body2">{m.analysisType || '-'}</Typography>,
      },
      {
        id: 'content',
        label: 'Content',
        minWidth: 420,
        render: (m) => (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {m.content}
          </Typography>
        ),
      },
      {
        id: 'createdAt',
        label: 'Created',
        minWidth: 180,
        render: (m) => <Typography variant="body2">{new Date(m.createdAt).toLocaleString()}</Typography>,
      },
    ],
    []
  );

  const correlationColumns: DataTableColumn<IncidentCorrelationDto>[] = useMemo(
    () => [
      {
        id: 'type',
        label: 'Type',
        minWidth: 180,
        render: (c) => <Typography variant="body2">{c.correlationType}</Typography>,
      },
      {
        id: 'entity',
        label: 'Related Entity',
        minWidth: 280,
        render: (c) => (
          <Box>
            <Typography variant="body2">{c.relatedEntityType || '-'}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {c.relatedEntityId || '-'}
            </Typography>
          </Box>
        ),
      },
      {
        id: 'confidence',
        label: 'Confidence',
        minWidth: 120,
        render: (c) => <Typography variant="body2">{c.confidenceScore.toFixed(2)}</Typography>,
      },
      {
        id: 'reason',
        label: 'Reason',
        minWidth: 320,
        render: (c) => <Typography variant="body2">{c.correlationReason || '-'}</Typography>,
      },
      {
        id: 'createdAt',
        label: 'Created',
        minWidth: 180,
        render: (c) => <Typography variant="body2">{new Date(c.createdAt).toLocaleString()}</Typography>,
      },
    ],
    []
  );

  return (
    <>
      <PageDetailLayout
        title={investigation?.title || 'Investigation'}
        breadcrumbs={[{ label: 'Investigations', href: ROUTES.INVESTIGATIONS }, { label: investigation?.title || 'Investigation' }]}
        backTo={{ label: 'Investigations', href: ROUTES.INVESTIGATIONS }}
        loading={loading}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        status={investigation ? { label: statusLabel(investigation.investigationStatus), color: statusColor(investigation.investigationStatus) } : undefined}
        chips={investigation ? [
          { label: severityLabel(investigation.severity), color: severityColor(investigation.severity) },
          ...(investigation.incidentNumber ? [{ label: `Incident: ${investigation.incidentNumber}`, variant: 'outlined' as const }] : []),
        ] : []}
        actions={investigation ? [
          { icon: <EditIcon />, label: 'Edit', onClick: () => navigate(`/investigations/${investigation.id}/edit`) },
          ...(investigation.investigationStatus !== InvestigationStatuses.Completed ? [{
            icon: <CompleteIcon />,
            label: 'Complete',
            onClick: handleComplete,
            variant: 'contained' as const,
          }] : []),
        ] : []}
        tabs={investigation ? [
          {
            label: 'Overview',
            value: 'overview',
            content: (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Agent: {investigation.agentName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(investigation.createdAt).toLocaleString()}
                </Typography>
                {investigation.investigationStartedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Started: {new Date(investigation.investigationStartedAt).toLocaleString()}
                  </Typography>
                )}
                {investigation.investigationCompletedAt && (
                  <Typography variant="body2" color="text.secondary">
                    Completed: {new Date(investigation.investigationCompletedAt).toLocaleString()}
                  </Typography>
                )}
                {investigation.description && (
                  <>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {investigation.description}
                    </Typography>
                  </>
                )}
              </Box>
            ),
          },
          {
            label: `Steps (${steps.length})`,
            value: 'steps',
            content: (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAddStepOpen(true)}
                  >
                    Add Step
                  </Button>
                </Box>
                <DataTable
                  columns={stepColumns}
                  rows={steps}
                  loading={false}
                  emptyState={<EmptyState title="No steps" description="No investigation steps found." />}
                />
              </Box>
            ),
          },
          {
            label: `Messages (${messages.length})`,
            value: 'messages',
            content: (
              <DataTable
                columns={messageColumns}
                rows={messages}
                loading={false}
                emptyState={<EmptyState title="No messages" description="No investigation messages found." />}
              />
            ),
          },
          {
            label: `Correlations (${correlations.length})`,
            value: 'correlations',
            content: (
              <DataTable
                columns={correlationColumns}
                rows={correlations}
                loading={false}
                emptyState={<EmptyState title="No correlations" description="No correlations found for this investigation." />}
              />
            ),
          },
          {
            label: 'Findings',
            value: 'findings',
            content: (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Root Cause Summary
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {investigation.rootCauseSummary || '—'}
                </Typography>

                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                  Remediation Steps
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {investigation.remediationSteps || '—'}
                </Typography>

                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                  Prevention Recommendations
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {investigation.preventionRecommendations || '—'}
                </Typography>
              </Box>
            ),
          },
        ] : []}
      />

      {id && (
        <InvestigationStepForm
          open={addStepOpen}
          investigationId={id}
          defaultOrder={steps.length + 1}
          onClose={() => setAddStepOpen(false)}
          onCreated={loadAll}
        />
      )}
    </>
  );
};
